import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  Box,
  Grid,
  CircularProgress,
  Backdrop,
  Typography,
} from "@material-ui/core";
import { GridColDef } from "@mui/x-data-grid-premium";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { AddData } from "src/app-redux/common-actions";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { ICompanyZipCodeItem } from "src/common/typings/companyProfile";
import AlertDialog from "src/components/AlertDialog/AlertDialog";
import CommonButtonOutLined from "src/components/CommonButton/CommonButtonOutLined";
import CommonButtonWithOutBor from "src/components/CommonButton/CommonButtonWOutBorder";
import CommonTextField from "src/components/CommonTextField/CommonTextField";
import CommonXGrid from "src/components/CommonXGrid/CommonXGrid";
import Heading from "./Heading";
import { useStyles } from "./styles";

type Props = {
  expand: boolean;
  updateExpandStatus: (type: number) => void;
};

export default function ZipCode({ expand, updateExpandStatus }: Props): React.ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [loader, setLoader] = useState(false);
  const [zipData, setZipData] = useState<any>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [zipId, setZipId] = useState<number>(0);
  const [selZip, setSelZip] = useState<string>("");

  const getData = async () => {
    try {
      setLoader(true);
      const results = await fetchWrapper.get("get-vendor-zip-by-vend-num");
      if (results?.vendorZip) {
        const vendorZip = results?.vendorZip as ICompanyZipCodeItem[];
        setZipData(vendorZip);
      }
      return setLoader(false);
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
  };

  const deleteRecord = async () => {
    setLoader(true);
    try {
      setLoader(true);
      const res = await fetchWrapper.delete(`delete-zip-by-id-by-vendor/${zipId}`);
      if (res.success) {
        dispatch(
          addNotificationAction({
            message: "Zip Code deleted successfully!",
            options: { variant: "success" },
          })
        );
        getData();
      } else {
        throw res.error;
      }
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
    setIsConfirmDialogOpen(false);
    setLoader(false);
    setZipId(0);
    getData();
  };
  const columns: GridColDef[] = [
    {
      field: "zipCode",
      headerName: "Zip Code",
      type: "string",
      width: 200,
      aggregable: false,
    },
    {
      field: "city",
      headerName: "City",
      type: "string",
      width: 200,
      aggregable: false,
    },
    {
      field: "state",
      headerName: "State",
      type: "string",
      width: 200,
      aggregable: false,
    },
    {
      field: "id",
      headerName: "Action",
      type: "string",
      width: 350,
      filterable: false,
      sortable: false,
      aggregable: false,
      groupable: false,
      renderCell: function customCell(params) {
        return (
          <CommonButtonWithOutBor
            onClick={() => {
              setZipId(params.row.id);
              setIsConfirmDialogOpen(true);
            }}
          >
            Delete
          </CommonButtonWithOutBor>
        );
      },
    },
  ];
  const submitForm = async () => {
    setLoader(true);
    const body = {
      zipCode: selZip,
    };
    try {
      await dispatch(
        AddData("/create-Zip-by-vendor", "Zip Code added successfully", JSON.stringify(body))
      );
      getData();
    } catch (error) {
      setLoader(false);
    }
    setLoader(false);
    setSelZip("");
  };
  useEffect(() => {
    if (expand) {
      getData();
      setSelZip("");
    }
  }, [expand]);
  return (
    <>
      <Backdrop className={classes.backdrop} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box border={1} mb={2} borderColor="grey.200">
        <Accordion square elevation={0} expanded={expand} onChange={() => updateExpandStatus(2)}>
          <Heading heading="Zip Codes" />
          <Box borderTop={1} mb={1} borderColor="grey.200"></Box>
          <AccordionDetails>
            <Grid container direction="row">
              <Grid item xs={12} md={3}>
                <CommonTextField
                  label="Zip Code"
                  onChange={(v: any) => {
                    setSelZip(v.target.value);
                  }}
                  name="zip"
                  required
                  value={selZip}
                  onlyAllowPostive
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box component="span" textAlign={"right"} ml={4}>
                  <CommonButtonOutLined
                    isGreen
                    disable={!selZip}
                    onClick={() => {
                      submitForm();
                    }}
                  >
                    Add New Zip
                  </CommonButtonOutLined>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box color="text.secondary" mt={2}>
                  <Typography className={classes.heading}>Zip Code List</Typography>
                </Box>
                <CommonXGrid
                  rows={zipData}
                  columns={columns}
                  defaultPerPageRecord={1000}
                  height={450}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        handleClose={() => {
          setIsConfirmDialogOpen(false);
        }}
        message={"Are you sure you want to Delete this Zip Code?"}
        primaryButtonText="Ok"
        onPrimaryButtonClick={() => {
          deleteRecord();
        }}
        heading={"Zip Code"}
      />
    </>
  );
}
